import React, { useEffect, useState, useCallback } from "react";
import QRCode from "react-qr-code";
import axiosInstance from "../utils/api";
import {
  FiCheckCircle, FiXCircle, FiPrinter, FiMail, FiCreditCard,
  FiX, FiClock, FiLogIn, FiLogOut, FiActivity, FiCheck, FiSend,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import toast from "react-hot-toast";
import { useBulkSend } from "../utils/Bulksendcontext";

const DEFAULT_LAYOUT = {
  logo: { visible: true, position: "top", size: 48, align: "left" },
  eventName: { visible: true, fontSize: 14, fontWeight: "extrabold", color: "#0f172a", align: "left" },
  tagline: { visible: true, text: "Badge d'Accès Officiel", fontSize: 9, color: "#64748b", opacity: 85 },
  name: { visible: true, fontSize: 14, fontWeight: "bold", color: "#ffffff" },
  email: { visible: true, fontSize: 10, color: "#ffffff" },
  cin: { visible: true, fontSize: 11, color: "#ffffff", prefix: "CIN. NO: " },
  qrCode: { visible: true, size: 90, border: true, borderColor: "#cbd5e1" },
  footer: { visible: true, text: "ACCESS ALL AREAS • 24 HOURS" },
};

const DEFAULT_FULL_ACCESS_CONFIG = {
  label: "STAFF",
  subLabel: "OFFICIAL STAFF",
  bodyColor: "#14532d",
  accentColor: "#166534",
  layout: null,
};

const merge = (target, source) => {
  if (!source) return target;
  const out = { ...target };
  for (const key of Object.keys(source)) {
    if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
      out[key] = merge(target[key] || {}, source[key]);
    } else if (source[key] !== undefined && source[key] !== null) {
      out[key] = source[key];
    }
  }
  return out;
};

// ─── Role config ──────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  "floor-manager": "Floor Manager",
  hse: "HSE",
  cleaning: "Cleaning",
  security: "Security",
  orga: "Organization",
};

const ROLE_GRADIENT = {
  "floor-manager": ["#1d4ed8", "#3b82f6"],
  hse: ["#059669", "#34d399"],
  cleaning: ["#d97706", "#fbbf24"],
  security: ["#c2410c", "#fb923c"],
  orga: ["#7c3aed", "#a78bfa"],
};

const ROLES = ["all", "floor-manager", "hse", "cleaning", "security", "orga"];

// ─── ScanLogsModal ────────────────────────────────────────────────────────────
const ScanLogsModal = ({ member, badge, onClose }) => {
  const logs = badge?.scanLogs || [];
  const grad = ROLE_GRADIENT[member.role] || ["#475569", "#94a3b8"];

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return {
      date: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }),
      time: d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    };
  };

  const grouped = logs.reduce((acc, log) => {
    const key = new Date(log.scannedAt).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    if (!acc[key]) acc[key] = [];
    acc[key].push(log);
    return acc;
  }, {});

  const entryCount = logs.filter(l => l.action === "entry").length;
  const exitCount = logs.filter(l => l.action === "exit").length;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(4px)" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        style={{ animation: "modalIn 0.2s ease-out" }}>
        <style>{`@keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }`}</style>

        <div className="p-5 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-base flex-shrink-0 shadow"
                style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}>
                {member.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <div className="font-bold text-slate-800 text-base leading-tight">{member.name}</div>
                <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <span className="inline-block px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded font-medium">
                    {ROLE_LABELS[member.role] || member.role}
                  </span>
                  {member.email && <span className="truncate max-w-[160px]">{member.email}</span>}
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600 flex-shrink-0">
              <FiX size={18} />
            </button>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            {[
              { bg: "bg-slate-50 border-slate-200", val: logs.length, valColor: "text-slate-800", icon: <FiActivity size={10} />, label: "Total Scans", labelColor: "text-slate-400" },
              { bg: "bg-green-50 border-green-200", val: entryCount, valColor: "text-green-700", icon: <FiLogIn size={10} />, label: "Entries", labelColor: "text-green-500" },
              { bg: "bg-orange-50 border-orange-200", val: exitCount, valColor: "text-orange-600", icon: <FiLogOut size={10} />, label: "Exits", labelColor: "text-orange-400" },
            ].map(s => (
              <div key={s.label} className={`${s.bg} border rounded-xl p-3 text-center`}>
                <div className={`text-xl font-bold ${s.valColor}`}>{s.val}</div>
                <div className={`text-[11px] mt-0.5 flex items-center justify-center gap-1 ${s.labelColor}`}>{s.icon} {s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
          <FiClock size={13} className="text-slate-400" />
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scan History</span>
        </div>

        <div className="flex-1 overflow-y-auto">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-3">
                <FiActivity size={22} className="text-slate-300" />
              </div>
              <p className="text-slate-500 font-medium text-sm">No scan logs yet</p>
              <p className="text-slate-400 text-xs mt-1">This badge hasn't been scanned</p>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {Object.entries(grouped)
                .sort((a, b) => new Date(b[1][0].scannedAt) - new Date(a[1][0].scannedAt))
                .map(([dateLabel, dayLogs]) => (
                  <div key={dateLabel}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide whitespace-nowrap">{dateLabel}</div>
                      <div className="flex-1 h-px bg-slate-100" />
                    </div>
                    <div className="space-y-2">
                      {[...dayLogs].sort((a, b) => new Date(b.scannedAt) - new Date(a.scannedAt)).map((log, i) => {
                        const { time } = formatDate(log.scannedAt);
                        const isEntry = log.action === "entry";
                        return (
                          <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${isEntry ? "bg-green-50 border-green-100" : "bg-orange-50 border-orange-100"}`}>
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isEntry ? "bg-green-100" : "bg-orange-100"}`}>
                              {isEntry ? <FiLogIn size={15} className="text-green-600" /> : <FiLogOut size={15} className="text-orange-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-bold uppercase tracking-wide ${isEntry ? "text-green-700" : "text-orange-600"}`}>{isEntry ? "Entry" : "Exit"}</span>
                                <span className="text-xs text-slate-400">·</span>
                                <span className="text-xs text-slate-500 font-medium truncate">Gate: <span className="text-slate-700 font-semibold">{log.gate}</span></span>
                              </div>
                              {log.agent && <div className="text-xs text-slate-400 mt-0.5 truncate">Agent: <span className="text-slate-600">{log.agent?.name || log.agent}</span></div>}
                            </div>
                            <div className="flex-shrink-0 text-right">
                              <div className={`text-xs font-mono font-semibold ${isEntry ? "text-green-700" : "text-orange-600"}`}>{time}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex-shrink-0">
          <p className="text-[11px] text-slate-400 text-center">Badge ID: <span className="font-mono text-slate-500">{badge?._id}</span></p>
        </div>
      </div>
    </div>
  );
};

const StaffCard = ({
  member, badge,
  onActivate, onDeactivate, onPrint, onWhatsApp, onCreateBadge, onViewLogs,
  actionLoading, creatingIds, sendingQueue, rateLimitInfo,
  selectionMode, isSelected, onToggleSelect, isSubAdmin
}) => {
  const hasBadge = !!badge;
  const isActive = badge?.status === "active";
  const badgeSentViaWhatsapp = !!badge?.whatsappSentAt;
  const grad = ROLE_GRADIENT[member.role] || ["#475569", "#94a3b8"];
  const hasPhone = !!member.phone;
  const canSelect = selectionMode && hasBadge && hasPhone;

  const handleCardClick = () => {
    if (selectionMode) { if (canSelect) onToggleSelect(badge._id); return; }
    if (hasBadge) onViewLogs(member, badge);
  };

  return (
    <div
      onClick={handleCardClick}
      className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden relative
        ${(hasBadge && !selectionMode) || canSelect ? "cursor-pointer" : ""}
        ${isSelected ? "border-emerald-400 ring-2 ring-emerald-300 shadow-emerald-100" : "border-slate-200"}
        ${selectionMode && !canSelect ? "opacity-50" : ""}
      `}
    >
      {selectionMode && (
        <div className={`absolute top-2.5 right-2.5 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
          ${isSelected ? "bg-emerald-500 border-emerald-500" : canSelect ? "bg-white border-slate-300" : "bg-slate-100 border-slate-200"}`}>
          {isSelected && <FiCheck size={13} className="text-white" />}
          {!canSelect && !isSelected && <FiX size={11} className="text-slate-300" />}
        </div>
      )}

      <div className={`h-0.5 w-full ${hasBadge ? (isActive ? "bg-green-400" : "bg-red-400") : "bg-slate-200"}`} />

      <div className="p-3 flex flex-col gap-2.5 flex-1">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm"
            style={{ background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})` }}>
            {member.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800 truncate text-sm leading-tight pr-7">{member.name}</div>
            {member.email && (
              <div className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5">
                <FiMail size={9} className="flex-shrink-0" />{member.email}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
            {ROLE_LABELS[member.role] || member.role}
          </span>
          {hasBadge && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-700 text-white">Staff</span>
          )}
          {hasBadge && badgeSentViaWhatsapp && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-1">
              <FiSend size={9} className="flex-shrink-0" /> Badge sent
            </span>
          )}
          {hasBadge ? (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1 ${isActive ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-600 border border-red-200"}`}>
              <span className={`w-1.5 h-1.5 rounded-full inline-block ${isActive ? "bg-green-500" : "bg-red-400"}`} />
              {isActive ? "Active" : "Inactive"}
            </span>
          ) : (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">No Badge</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          {member.phone ? (
            <div className="text-xs text-slate-400 flex items-center gap-1">
              <FaWhatsapp size={9} className="text-emerald-400 flex-shrink-0" />{member.phone}
            </div>
          ) : selectionMode && (
            <div className="text-xs text-red-400 flex items-center gap-1">
              <FaWhatsapp size={9} className="flex-shrink-0" /> No phone number
            </div>
          )}
        </div>
      </div>

      {!selectionMode && !isSubAdmin && (
        <div className="px-3 pb-3 flex flex-col gap-1.5">
          {!hasBadge ? (
            <button
              onClick={(e) => { e.stopPropagation(); onCreateBadge(member._id); }}
              disabled={creatingIds.has(member._id)}
              className="w-full py-1.5 px-2 bg-slate-700 hover:bg-slate-800 disabled:bg-slate-400 text-white text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1.5"
            >
              {creatingIds.has(member._id)
                ? <><div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> Creating...</>
                : <><svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg> Create Badge</>}
            </button>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={(e) => { e.stopPropagation(); onActivate(badge._id, member._id); }}
                  disabled={isActive || actionLoading === badge._id}
                  className={`py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1
                    ${isActive ? "bg-slate-100 text-slate-400 cursor-not-allowed" : actionLoading === badge._id ? "bg-green-500 text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}>
                  {actionLoading === badge._id ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> : <FiCheckCircle size={12} />}
                  Activate
                </button>
                <button onClick={(e) => { e.stopPropagation(); onDeactivate(badge._id, member._id); }}
                  disabled={!isActive || actionLoading === badge._id}
                  className={`py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1
                    ${!isActive ? "bg-slate-100 text-slate-400 cursor-not-allowed" : actionLoading === badge._id ? "bg-red-500 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                  {actionLoading === badge._id ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> : <FiXCircle size={12} />}
                  Deactivate
                </button>
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                <button onClick={(e) => { e.stopPropagation(); onPrint(member, badge); }}
                  className="py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1">
                  <FiPrinter size={12} /> Print
                </button>
                <button onClick={(e) => { e.stopPropagation(); onWhatsApp(member, badge); }}
                  disabled={sendingQueue.has(badge._id) || !!rateLimitInfo || !member.phone}
                  className={`py-1.5 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-1
                    ${sendingQueue.has(badge._id) || rateLimitInfo || !member.phone ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
                  {sendingQueue.has(badge._id)
                    ? <><div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" /> Sending</>
                    : <><FaWhatsapp size={12} /> WhatsApp</>}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const StaffBadgesTab = ({ eventId, isSubAdmin }) => {
  const { session, startBulkSend } = useBulkSend();
  const isQueueRunning = !!session && !session.done;

  const [staff, setStaff] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [badgesMap, setBadgesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [eventName, setEventName] = useState("");
  const [badgeConfig, setBadgeConfig] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [sendingQueue, setSendingQueue] = useState(new Set());
  const [creatingIds, setCreatingIds] = useState(new Set());
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [logsModal, setLogsModal] = useState(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    if (!rateLimitInfo?.retryAfter) return;
    const timer = setInterval(() => {
      setRateLimitInfo(prev => {
        if (!prev || prev.retryAfter <= 1) { clearInterval(timer); return null; }
        return { ...prev, retryAfter: prev.retryAfter - 1 };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [rateLimitInfo?.retryAfter]);

  useEffect(() => {
    if (!eventId) return;
    axiosInstance.get(`/event/${eventId}`)
      .then(res => { setBadgeConfig(res.data.event?.badgeConfig || null); console.log(res.data.event?.badgeConfig);
      })
      .catch(() => setEventName("Event"));
  }, [eventId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/auth/staff/${eventId}`);
      const list = res.data.staff || [];
      setAllStaff(list);
      const results = await Promise.allSettled(list.map(m => axiosInstance.get(`/badge/by-staff/${m._id}`)));
      const map = {};
      results.forEach((r, i) => {
        map[list[i]._id] = r.status === "fulfilled"
          ? (r.value.data?.badges || []).find(b => b.type === "full_access") || null
          : null;
      });
      setBadgesMap(map);
    } catch { toast.error("Failed to load staff"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, [eventId]);

  useEffect(() => {
    let result = allStaff;
    const q = search.toLowerCase().trim();
    if (q) result = result.filter(m => m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q));
    if (roleFilter !== "all") result = result.filter(m => m.role === roleFilter);
    if (statusFilter !== "all") result = result.filter(m => (badgesMap[m._id] ? badgesMap[m._id].status : "none") === statusFilter);
    setStaff(result);
  }, [search, roleFilter, statusFilter, allStaff, badgesMap]);

  const createBadge = async (memberId) => {
    setCreatingIds(p => new Set(p).add(memberId));
    try {
      const res = await axiosInstance.post(`/badge/staff-platform/${memberId}`);
      setBadgesMap(p => ({ ...p, [memberId]: res.data.badge }));
      toast.success("Badge created successfully");
    } catch (err) { toast.error(err.response?.data?.message || "Failed to create badge"); }
    finally { setCreatingIds(p => { const s = new Set(p); s.delete(memberId); return s; }); }
  };

  const updateStatus = async (badgeId, status, memberId) => {
    setActionLoading(badgeId);
    try {
      const res = await axiosInstance.put(`/badge/${badgeId}`, { status });
      setBadgesMap(p => ({ ...p, [memberId]: res.data }));
      toast.success(`Badge ${status === "active" ? "activated" : "deactivated"} successfully`);
    } catch { toast.error("Failed to update badge status"); }
    finally { setActionLoading(null); }
  };

  const sendWhatsappBadge = async (member, badge) => {
    if (sendingQueue.has(badge._id)) { toast.error("Already sending this badge"); return; }
    setSendingQueue(p => new Set([...p, badge._id]));
    const tid = toast.loading("Generating PDF and sending via WhatsApp...");
    try {
      const res = await axiosInstance.post("/badge/send-whatsapp", { phone: member.phone, badgeId: badge._id, name: member.name, eventName, eventId, badgeConfig });
      const ws = res.data?.whatsappSentAt;
      if (ws) {
        setBadgesMap((p) => ({
          ...p,
          [member._id]: { ...p[member._id], whatsappSentAt: ws },
        }));
      }
      toast.dismiss(tid);
      toast.success("Badge PDF sent successfully via WhatsApp");
      setRateLimitInfo(null);
    } catch (err) {
      toast.dismiss(tid);
      if (err.response?.status === 422) {
        toast.error(`Invalid phone number: ${member.phone}`, { duration: 6000 });
      } else if (err.response?.status === 429) {
        const r = err.response.data?.retry_after || 60;
        setRateLimitInfo({ retryAfter: r });
        toast.error(`Rate limit reached. Wait ${r}s before sending.`, { duration: 5000 });
      } else {
        toast.error(err.response?.data?.message || "Failed to send badge PDF");
      }
    } finally { setSendingQueue(p => { const s = new Set(p); s.delete(badge._id); return s; }); }
  };

  const selectablePairs = useCallback(() =>
    staff.map(m => ({ member: m, badge: badgesMap[m._id] || null }))
      .filter(({ badge, member }) => !!badge && !!member.phone),
    [staff, badgesMap]);

  const toggleSelect = (badgeId) => setSelectedIds(prev => { const n = new Set(prev); n.has(badgeId) ? n.delete(badgeId) : n.add(badgeId); return n; });
  const selectAll = () => setSelectedIds(new Set(selectablePairs().map(p => p.badge._id)));
  const deselectAll = () => setSelectedIds(new Set());
  const exitSelection = () => { setSelectionMode(false); setSelectedIds(new Set()); };

  const handleBulkSend = () => {
    const chosen = selectablePairs().filter(p => selectedIds.has(p.badge._id));
    if (chosen.length === 0) { toast.error("No badges with phone numbers selected"); return; }
    startBulkSend({
      badges: chosen.map(({ member, badge }) => ({
        badge: { _id: badge._id },
        person: { name: member.name, phone: member.phone, memberId: member._id },
      })),
      eventName,
      eventId,
      onSendSuccess: ({ memberId, whatsappSentAt }) => {
        if (!memberId) return;
        setBadgesMap((p) => {
          const prev = p[memberId];
          if (!prev) return p;
          return { ...p, [memberId]: { ...prev, whatsappSentAt } };
        });
      },
    });
    exitSelection();
  };

  const printBadge = (person, badge) => {
    const savedTypeConfig = badgeConfig?.types?.full_access || {};
    const typeConfig = { ...DEFAULT_FULL_ACCESS_CONFIG, ...savedTypeConfig };
    const globalLayout = badgeConfig?.globalLayout ? merge(DEFAULT_LAYOUT, badgeConfig.globalLayout) : DEFAULT_LAYOUT;
    const layout = typeConfig.layout ? merge(globalLayout, typeConfig.layout) : globalLayout;

    const bodyColor = typeConfig.bodyColor || DEFAULT_FULL_ACCESS_CONFIG.bodyColor;
    const accentColor = typeConfig.accentColor || DEFAULT_FULL_ACCESS_CONFIG.accentColor;
    const label = typeConfig.label || "STAFF";
    const subLabel = typeConfig.subLabel || "OFFICIAL STAFF";
    const logoUrl = badgeConfig?.logo;
    const font = globalLayout.font || "ui-sans-serif, system-ui, sans-serif";
    const qrSize = layout.qrCode?.size || 90;
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize * 2}x${qrSize * 2}&data=${encodeURIComponent(badge._id)}`;

    // ── Header config ──────────────────────────────────────────────────────
    const DEFAULT_HEADER_LOCAL = {
      image: null, height: 90, overlay: false, overlayColor: "#000000", overlayOpacity: 40,
      custom: {
        bgColor: "#0f172a", bgColor2: "#1e3a5f", gradientDir: "to right",
        showLogo: true, logoSize: 44,
        showEventName: true, eventNameFontSize: 15, eventNameColor: "#ffffff", eventNameWeight: "bold",
        showTagline: false, taglineText: "Badge d'Accès Officiel", taglineFontSize: 9, taglineColor: "#94a3b8",
        layout: "logo-left", padding: 16,
      },
    };
    const hdr = badgeConfig?.header
      ? merge(DEFAULT_HEADER_LOCAL, badgeConfig.header)
      : DEFAULT_HEADER_LOCAL;
    const headerHeight = hdr.height || 90;
    const hasImage = !!hdr.image;
    const c = hdr.custom || DEFAULT_HEADER_LOCAL.custom;

    // ── Build header HTML ──────────────────────────────────────────────────
    let headerInnerHtml = "";
    if (hasImage) {
      const overlayHtml = hdr.overlay
        ? `<div style="position:absolute;inset:0;background:${hdr.overlayColor || "#000"};opacity:${(hdr.overlayOpacity ?? 40) / 100};"></div>`
        : "";
      headerInnerHtml = `
      <div style="position:relative;width:100%;height:${headerHeight}px;overflow:hidden;flex-shrink:0;line-height:0;">
        <img src="${hdr.image}" style="width:100%;height:100%;object-fit:cover;display:block;" />
        ${overlayHtml}
      </div>`;
    } else {
      // Custom gradient header
      const pad = c.padding || 16;
      const bg = `linear-gradient(${c.gradientDir || "to right"}, ${c.bgColor || "#0f172a"}, ${c.bgColor2 || "#1e3a5f"})`;
      const logoEl = logoUrl && c.showLogo
        ? `<img src="${logoUrl}" style="height:${c.logoSize || 44}px;object-fit:contain;flex-shrink:0;" />`
        : "";
      const nameFW = c.eventNameWeight === "extrabold" ? 800 : c.eventNameWeight === "bold" ? 700 : 600;
      const nameEl = c.showEventName
        ? `<div style="display:flex;flex-direction:column;justify-content:center;overflow:hidden;">
           <div style="font-size:${c.eventNameFontSize || 15}px;font-weight:${nameFW};color:${c.eventNameColor || "#fff"};line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
             ${eventName || "Event"}
           </div>
           ${c.showTagline ? `<div style="font-size:${c.taglineFontSize || 9}px;color:${c.taglineColor || "#94a3b8"};margin-top:3px;letter-spacing:0.5px;">${c.taglineText || "Badge d'Accès Officiel"}</div>` : ""}
         </div>`
        : "";

      let innerLayout = "";
      switch (c.layout) {
        case "logo-left":
          innerLayout = `<div style="display:flex;align-items:center;gap:12px;padding:0 ${pad}px;width:100%;height:100%;">${logoEl}${nameEl}</div>`;
          break;
        case "logo-right":
          innerLayout = `<div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 ${pad}px;width:100%;height:100%;">${nameEl}${logoEl}</div>`;
          break;
        case "logo-top":
          innerLayout = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;padding:${pad / 2}px ${pad}px;width:100%;height:100%;">${logoEl}<div style="text-align:center;">${nameEl}</div></div>`;
          break;
        case "logo-only":
          innerLayout = `<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">${logoEl}</div>`;
          break;
        case "text-only":
          innerLayout = `<div style="display:flex;align-items:center;justify-content:center;padding:0 ${pad}px;width:100%;height:100%;"><div style="text-align:center;">${nameEl}</div></div>`;
          break;
        default:
          innerLayout = `<div style="display:flex;align-items:center;gap:12px;padding:0 ${pad}px;width:100%;height:100%;">${logoEl}${nameEl}</div>`;
      }

      headerInnerHtml = `
      <div style="width:100%;height:${headerHeight}px;background:${bg};flex-shrink:0;display:flex;align-items:center;overflow:hidden;">
        ${innerLayout}
      </div>`;
    }

    // ── Rest of badge fields ───────────────────────────────────────────────
    const cinPrefix = layout.cin?.prefix || "CIN. NO: ";
    const srNoHtml = layout.cin?.visible && person.cin
      ? `<div style="font-size:${layout.cin?.fontSize || 11}px;color:${layout.cin?.color || "#fff"};opacity:0.8;margin-bottom:8px;letter-spacing:1px;font-weight:600;">${cinPrefix}<span style="font-family:monospace;">${person.cin}</span></div>`
      : "";

    const nameFW2 = layout.name?.fontWeight === "bold" ? 700 : 600;
    const nameHtml = layout.name?.visible
      ? `<div style="font-size:${layout.name?.fontSize || 14}px;font-weight:${nameFW2};color:${layout.name?.color || "#fff"};margin-bottom:2px;">${person.name}</div>`
      : "";

    const emailHtml = layout.email?.visible && person.email
      ? `<div style="font-size:${layout.email?.fontSize || 10}px;color:${layout.email?.color || "#fff"};opacity:0.65;">${person.email}</div>`
      : "";

    const footerHtml = layout.footer?.visible
      ? `<div style="z-index:1;width:100%;border-top:1px solid rgba(255,255,255,0.15);padding-top:10px;text-align:center;"><div style="font-size:11px;font-weight:800;color:#fff;letter-spacing:1.5px;text-transform:uppercase;">${layout.footer?.text || "ACCESS ALL AREAS"}</div></div>`
      : "";

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
@page { size:105mm 148mm; margin:0; }
*,*::before,*::after { box-sizing:border-box; margin:0; padding:0; }
html,body { width:105mm; height:148mm; overflow:hidden; background:#fff; font-family:${font}; }
.badge { width:105mm; height:148mm; display:flex; flex-direction:column; background:#fff; overflow:hidden; }
.body  { flex:1; background:${bodyColor}; display:flex; flex-direction:column; align-items:center; justify-content:space-between; padding:20px 18px 16px; position:relative; overflow:hidden; }
.texture { position:absolute; inset:0; opacity:0.04; background-image:repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%); background-size:20px 20px; pointer-events:none; }
.type-wrap { text-align:center; z-index:1; }
.qr-wrap   { text-align:center; z-index:1; width:100%; }
.qr-box    { background:#fff; padding:8px; border:3px solid ${accentColor}; border-radius:8px; box-shadow:0 4px 16px rgba(0,0,0,0.4); display:inline-block; }
.name-wrap { text-align:center; z-index:1; }
</style>
</head><body><div class="badge">
  ${headerInnerHtml}
  <div class="body">
    <div class="texture"></div>
    <div class="type-wrap">
      <div style="font-size:9px;color:#fff;opacity:0.55;letter-spacing:3px;text-transform:uppercase;margin-bottom:6px;">${subLabel}</div>
      <div style="font-size:${label.length > 9 ? 26 : 34}px;font-weight:900;color:#fff;letter-spacing:0.5px;line-height:1.05;text-transform:uppercase;">${label}</div>
    </div>
    <div class="qr-wrap">
      ${srNoHtml}
      <div class="qr-box"><img src="${qrUrl}" width="${qrSize}" height="${qrSize}" /></div>
    </div>
    <div class="name-wrap">
      ${nameHtml}
      ${emailHtml}
    </div>
    ${footerHtml}
  </div>
</div>
<script>
  var img = document.querySelector('.qr-box img');
  function doPrint(){ window.focus(); window.print(); }
  if(img){ img.onload = doPrint; img.onerror = doPrint; } else { doPrint(); }
<\/script>
</body></html>`;

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open(); doc.write(html); doc.close();
    iframe.contentWindow.addEventListener("afterprint", () => document.body.removeChild(iframe));
  };

  const stats = {
    total: allStaff.length,
    active: allStaff.filter(m => badgesMap[m._id]?.status === "active").length,
    inactive: allStaff.filter(m => badgesMap[m._id]?.status === "inactive").length,
    noBadge: allStaff.filter(m => !badgesMap[m._id]).length,
  };

  const selectableCount = selectablePairs().length;

  return (
    <>
      <div className="space-y-5">

        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
              Staff Badges
              {stats.total > 0 && (
                <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{stats.total}</span>
              )}
            </h3>
            <p className="text-sm text-slate-400 mt-0.5">Full-access badges for platform staff members</p>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={fetchAll} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-white border border-slate-300 text-slate-600 rounded-lg hover:border-slate-400 hover:bg-slate-50 transition-all disabled:opacity-50">
              <svg className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>

            {!selectionMode ? (
              !isSubAdmin && (<button onClick={() => setSelectionMode(true)} disabled={selectableCount === 0 || isQueueRunning}
                title={isQueueRunning ? "A bulk send is already in progress" : undefined}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-lg transition-all">
                <FaWhatsapp size={13} /> Bulk Send
              </button>)
            ) : (
              <button onClick={exitSelection}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-all">
                <FiX size={13} /> Cancel
              </button>
            )}
          </div>
        </div>

        {selectionMode && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-emerald-800">
                {selectedIds.size > 0 ? `${selectedIds.size} badge${selectedIds.size > 1 ? "s" : ""} selected` : "Tap cards to select"}
              </span>
              <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                {selectableCount} eligible (with phone)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={selectAll} className="text-xs text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">Select all</button>
              <span className="text-emerald-300">|</span>
              <button onClick={deselectAll} className="text-xs text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">Deselect all</button>
              <button onClick={handleBulkSend} disabled={selectedIds.size === 0}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
                  ${selectedIds.size === 0 ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}>
                <FiSend size={13} /> Send {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
              </button>
            </div>
          </div>
        )}

        {rateLimitInfo && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg flex items-start gap-3">
            <svg className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm text-amber-700 font-semibold">Rate Limit Active</p>
              <p className="text-xs text-amber-600 mt-0.5">Next send available in {rateLimitInfo.retryAfter}s.</p>
            </div>
            <button onClick={() => setRateLimitInfo(null)} className="text-amber-400 hover:text-amber-600"><FiX size={18} /></button>
          </div>
        )}

        <div className="grid grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total, dot: "bg-slate-400", bg: "bg-slate-50" },
            { label: "Active", value: stats.active, dot: "bg-green-500", bg: "bg-green-50" },
            { label: "Inactive", value: stats.inactive, dot: "bg-red-400", bg: "bg-red-50" },
            { label: "No Badge", value: stats.noBadge, dot: "bg-amber-400", bg: "bg-amber-50" },
          ].map(s => (
            <div key={s.label} className={`${s.bg} border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.dot}`} />
              <div>
                <div className="text-xl font-bold text-slate-800 leading-none">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
            <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {ROLES.map(r => (
              <button key={r} onClick={() => setRoleFilter(r)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${roleFilter === r ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                {r === "all" ? "All Roles" : ROLE_LABELS[r] || r}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {["all", "active", "inactive", "none"].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${statusFilter === s ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`}>
                {s === "all" ? "All Status" : s === "none" ? "No Badge" : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4" />
            <p className="text-slate-500">Loading staff badges...</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
            <svg className="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-slate-500 font-medium">No staff members found</p>
            <p className="text-slate-400 text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {staff.map(member => {
              const badge = badgesMap[member._id] || null;
              return (
                <StaffCard
                  key={member._id}
                  member={member}
                  badge={badge}
                  onActivate={(badgeId, memberId) => updateStatus(badgeId, "active", memberId)}
                  onDeactivate={(badgeId, memberId) => updateStatus(badgeId, "inactive", memberId)}
                  onPrint={printBadge}
                  onWhatsApp={sendWhatsappBadge}
                  onCreateBadge={createBadge}
                  onViewLogs={(m, b) => setLogsModal({ member: m, badge: b })}
                  actionLoading={actionLoading}
                  creatingIds={creatingIds}
                  sendingQueue={sendingQueue}
                  rateLimitInfo={rateLimitInfo}
                  selectionMode={selectionMode}
                  isSelected={selectedIds.has(badge?._id)}
                  onToggleSelect={toggleSelect}
                  isSubAdmin={isSubAdmin}
                />
              );
            })}
          </div>
        )}
      </div>

      {logsModal && (
        <ScanLogsModal member={logsModal.member} badge={logsModal.badge} onClose={() => setLogsModal(null)} />
      )}
    </>
  );
};

export default StaffBadgesTab;